import { useEffect, useState } from "react";
import "./App.css";
import AdminSidebar from "./components/AdminSidebar";
import HeaderBar from "./components/HeaderBar";
import CallInputPanel from "./components/CallInputPanel";
import OutputPanel from "./components/OutputPanel";
import Login from "./components/Login";

function App() {
  const [isAdminOpen, setIsAdminOpen] = useState(false);

  // Voice/call settings
  const [callType, setCallType] = useState("overdue");
  const [inputMode, setInputMode] = useState("file");

  // File upload
  const [fileName, setFileName] = useState("");
  const [fileObject, setFileObject] = useState(null);

  // Run state
  const [runConfig, setRunConfig] = useState(null);
  const [inputCollapsed, setInputCollapsed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // User state
  const [user, setUser] = useState(null);

  // Languages fetched from backend
  const [languages, setLanguages] = useState([]);
  const [langsLoading, setLangsLoading] = useState(true);

  // Eleven language code (lowercase like "ro", "en")
  const [selectedCountry, setSelectedCountry] = useState("ro");

  const handleLogin = (username) => setUser({ username });

  const handleToggleAdmin = () => setIsAdminOpen((prev) => !prev);
  const handleCountryChange = (event) => setSelectedCountry(event.target.value);
  const handleCallTypeChange = (event) => setCallType(event.target.value);
  const handleInputModeChange = (mode) => setInputMode(mode);

  const handleFileChange = (file) => {
    setFileName(file.name);
    setFileObject(file);
  };

  // Fetch languages AFTER login (so app doesn't call APIs on login screen)
  useEffect(() => {
    if (!user) return;

    const loadLanguages = async () => {
      try {
        setLangsLoading(true);

        const resp = await fetch("/api/get-languages");
        const data = await resp.json();

        if (!resp.ok || !data?.success) {
          throw new Error(data?.error || `Failed to load languages (${resp.status})`);
        }

        const list = Array.isArray(data.languages) ? data.languages : [];
        setLanguages(list);

        // Ensure selectedCountry exists in list
        const exists = list.some((l) => l.code === selectedCountry);
        if (!exists && list.length) {
          setSelectedCountry(list[0].code);
        }
      } catch (err) {
        console.error("❌ get-languages failed:", err);

        // fallback so UI still works
        const fallback = [
          { code: "ro", name: "Romanian" },
          { code: "en", name: "English" },
        ];
        setLanguages(fallback);

        if (!fallback.some((l) => l.code === selectedCountry)) {
          setSelectedCountry(fallback[0].code);
        }
      } finally {
        setLangsLoading(false);
      }
    };

    loadLanguages();
  }, [user]);

  const handleStart = async (payload) => {
    if (payload.mode === "file") {
      if (!fileObject) {
        alert("Please select a file first!");
        return;
      }

      setIsUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", fileObject);

        formData.append("country", payload.country);

        const response = await fetch("/api/upload-calls", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
        const data = await response.json();
        if (!data.success) throw new Error(data.error || "Upload failed");

        setRunConfig({ ...payload, uploadResponse: data });
        setInputCollapsed(true);
      } catch (error) {
        console.error("❌ Error uploading file:", error);
        alert(`Failed to upload file: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    } else {
      setRunConfig(payload);
      setInputCollapsed(true);
    }
  };

  const handleToggleInputSidebar = () => setInputCollapsed((prev) => !prev);

  // --- LOGIN FIRST ---
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const openedFromOutput = !!runConfig && !inputCollapsed;

  return (
    <div className="app-root">
      <AdminSidebar isOpen={isAdminOpen} onToggle={handleToggleAdmin} user={user} />

      <div className="main-layout">
        <HeaderBar />

        {isUploading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              color: "white",
              fontSize: "24px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ marginBottom: "12px" }}>📤</div>
              <div>Uploading and processing file...</div>
            </div>
          </div>
        )}

        {!runConfig ? (
          <main className="panels-container layout-input-full">
            <CallInputPanel
              languages={languages}
              languagesLoading={langsLoading}
              selectedCountry={selectedCountry}
              onCountryChange={(e) => setSelectedCountry(e.target.value)}
              callType={callType}
              onCallTypeChange={handleCallTypeChange}
              inputMode={inputMode}
              onInputModeChange={handleInputModeChange}
              fileName={fileName}
              onFileChange={handleFileChange}
              onStart={handleStart}
            />
          </main>
        ) : (
          <main className="panels-container layout-with-sidebar">
            <div className={`input-sidebar ${inputCollapsed ? "collapsed" : "expanded"}`}>
              <CallInputPanel
                languages={languages}
                languagesLoading={langsLoading}
                selectedCountry={selectedCountry}
                onCountryChange={handleCountryChange}
                callType={callType}
                onCallTypeChange={handleCallTypeChange}
                inputMode={inputMode}
                onInputModeChange={handleInputModeChange}
                fileName={fileName}
                onFileChange={handleFileChange}
                onStart={handleStart}
                collapsed={inputCollapsed}
                compact={openedFromOutput}
                onToggleCollapse={handleToggleInputSidebar}
              />
            </div>

            <OutputPanel runConfig={runConfig} />
          </main>
        )}
      </div>
    </div>
  );
}

export default App;
